// Copyright (c) Microsoft. All rights reserved.

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ChatMemorySource, DocumentUploadState } from '../../../libs/models/ChatMemorySource';
import { Citation, IChatMessage, UserFeedback } from '../../../libs/models/ChatMessage';
import { IChatUser } from '../../../libs/models/ChatUser';
import { ISiteInfo } from '../../../libs/models/SelectedSites';
import { ChatState } from './ChatState';
import {
    ConversationAuthoritiveDocumentsChange,
    ConversationBotProfilePictureChange,
    ConversationHiddenStatusChange,
    ConversationInputChange,
    Conversations,
    ConversationScopeChange,
    ConversationSiteSourcesChange,
    ConversationsState,
    ConversationTitleChange,
    initialState,
    UpdatePluginStatePayload,
} from './ConversationsState';

export const conversationsSlice = createSlice({
    name: 'conversations',
    initialState,
    reducers: {
        updateMessageCitations: (
            state: ConversationsState,
            action: PayloadAction<{ chatId: string; messageId: string; citations: Citation[] }>,
        ) => {
            const { chatId, messageId, citations } = action.payload;
            const conversation = state.conversations[chatId];
            const conversationMessage = conversation.messages?.find((m) => m.id === messageId);

            if (conversationMessage) {
                conversationMessage.citations = citations;
            }
        },
        setConversations: (state: ConversationsState, action: PayloadAction<Conversations>) => {
            state.conversations = action.payload;
        },
        setConversationMessages: (
            state: ConversationsState,
            action: PayloadAction<{ chatId: string; messages: IChatMessage[] }>,
        ) => {
            const { chatId, messages } = action.payload;
            state.conversations[chatId].messages = messages;
        },
        setConversationParticipants: (
            state: ConversationsState,
            action: PayloadAction<{ chatId: string; users: IChatUser[] }>,
        ) => {
            const { chatId, users } = action.payload;
            state.conversations[chatId].users = users;
        },
        setConversationUserFeedback: (
            state: ConversationsState,
            action: PayloadAction<{ chatId: string; userFeedback: UserFeedback[] }>,
        ) => {
            const { chatId, userFeedback } = action.payload;
            state.conversations[chatId].userFeedback = userFeedback;
        },
        addUserFeedback: (
            state: ConversationsState,
            action: PayloadAction<{ chatId: string; userFeedback: UserFeedback }>,
        ) => {
            const { chatId, userFeedback } = action.payload;
            state.conversations[chatId].userFeedback = (state.conversations[chatId].userFeedback ?? []).concat(
                userFeedback,
            );
        },
        editConversationTitle: (state: ConversationsState, action: PayloadAction<ConversationTitleChange>) => {
            const id = action.payload.id;
            const newTitle = action.payload.newTitle;
            state.conversations[id].title = newTitle;
            frontLoadChat(state, id);
        },
        editConversationScope: (state: ConversationsState, action: PayloadAction<ConversationScopeChange>) => {
            const id = action.payload.id;
            const newScope = action.payload.newScope;

            state.conversations[id].chatScope = newScope;
            frontLoadChat(state, id);
        },
        editConversationBotProfilePicture: (
            state: ConversationsState,
            action: PayloadAction<ConversationBotProfilePictureChange>,
        ) => {
            const id = action.payload.id;
            const newProfilePicture = action.payload.newProfilePicture;
            state.conversations[id].botProfilePicture = newProfilePicture;
        },
        setConversationAsHidden(state: ConversationsState, action: PayloadAction<ConversationHiddenStatusChange>) {
            const { id } = action.payload;
            state.conversations[id].hidden = action.payload.newHiddenState;
        },
        editConversationSiteSource: (
            state: ConversationsState,
            action: PayloadAction<ConversationSiteSourcesChange>,
        ) => {
            const id: string = action.payload.id;
            const newSiteSources: ISiteInfo[] = action.payload.siteSources;
            state.conversations[id].selectedSites = newSiteSources;
        },
        editConversationAuthoritiveDocuments: (
            state: ConversationsState,
            action: PayloadAction<ConversationAuthoritiveDocumentsChange>,
        ) => {
            const id = action.payload.id;
            const newSiteSources = action.payload.newDocuments;
            state.conversations[id].authoritiveDocuments = newSiteSources;
        },
        editConversationInput: (state: ConversationsState, action: PayloadAction<ConversationInputChange>) => {
            const id = action.payload.id;
            const newInput = action.payload.newInput;
            state.conversations[id].input = newInput;
        },
        editConversationMemoryBalance: (
            state: ConversationsState,
            action: PayloadAction<{ id: string; memoryBalance: number }>,
        ) => {
            const id = action.payload.id;
            const newMemoryBalance = action.payload.memoryBalance;
            state.conversations[id].memoryBalance = newMemoryBalance;
        },
        editConversationIsSaved: (
            state: ConversationsState,
            action: PayloadAction<{ id: string; isSaved: boolean }>,
        ) => {
            const id = action.payload.id;
            const isSaved = action.payload.isSaved;
            state.conversations[id].isSaved = isSaved;
        },
        setSelectedConversation: (state: ConversationsState, action: PayloadAction<string>) => {
            state.selectedId = action.payload;
        },
        toggleMultiUserConversations: (state: ConversationsState) => {
            const keys = Object.keys(state.conversations);
            keys.forEach((key) => {
                if (state.conversations[key].users.length > 1) {
                    state.conversations[key].hidden = !state.conversations[key].hidden;
                }
            });
        },
        addConversation: (state: ConversationsState, action: PayloadAction<ChatState>) => {
            const newId = action.payload.id;
            state.conversations = { [newId]: action.payload, ...state.conversations };
            state.selectedId = newId;
        },
        addUserToConversation: (
            state: ConversationsState,
            action: PayloadAction<{ user: IChatUser; chatId: string }>,
        ) => {
            const { user, chatId } = action.payload;
            state.conversations[chatId].users.push(user);
            state.conversations[chatId].userDataLoaded = false;
        },
        setChatMemorySources: (
            state: ConversationsState,
            action: PayloadAction<{ chatMemorySources: ChatMemorySource[]; chatId: string }>,
        ) => {
            const { chatMemorySources, chatId } = action.payload;
            state.conversations[chatId].chatMemorySources = chatMemorySources;
        },
        setChatMemorySourceUploadComplete: (
            state: ConversationsState,
            action: PayloadAction<{ chatId: string; memorySourceId: string; message: IChatMessage }>,
        ) => {
            const { chatId, memorySourceId, message } = action.payload;
            const memoryIndex = state.conversations[chatId].chatMemorySources?.findIndex(
                (x) => x.id === memorySourceId,
            );

            if (memoryIndex !== undefined && memoryIndex !== -1) {
                const chatMemorySources = state.conversations[chatId].chatMemorySources;
                if (chatMemorySources?.[memoryIndex]) {
                    chatMemorySources[memoryIndex].documentUploadState = DocumentUploadState.Complete;
                }
                state.conversations[chatId].chatMemorySources = chatMemorySources;
            }
            updateConversation(state, chatId, message);
        },
        setUsersLoaded: (state: ConversationsState, action: PayloadAction<string>) => {
            state.conversations[action.payload].userDataLoaded = true;
        },
        /*
         * addMessageToConversationFromUser() and addMessageToConversationFromServer() both update the conversations state.
         * However they are for different purposes. The former action is for updating the conversation from the
         * webapp and will be captured by the SignalR middleware and the payload will be broadcasted to all clients
         * in the same group.
         * The addMessageToConversationFromServer() action is triggered by the SignalR middleware when a response is received
         * from the webapi.
         */
        addMessageToConversationFromUser: (
            state: ConversationsState,
            action: PayloadAction<{ message: IChatMessage; chatId: string }>,
        ) => {
            const { message, chatId } = action.payload;
            updateConversation(state, chatId, message);
        },
        addMessagesToConversation: (
            state: ConversationsState,
            action: PayloadAction<{ messages: IChatMessage[]; chatId: string }>,
        ) => {
            const { messages, chatId } = action.payload;
            updateConversationMessages(state, chatId, messages);
        },

        setIsLoadingMessages(state: ConversationsState, action: PayloadAction<boolean>) {
            state.isLoadingMessages = action.payload;
        },
        setIsLoadingChats(state: ConversationsState, action: PayloadAction<boolean>) {
            state.isLoadingChats = action.payload;
        },
        setInitialChatsLoaded(state: ConversationsState, action: PayloadAction<boolean>) {
            state.initialChatsLoaded = action.payload;
        },
        addMessageToConversationFromServer: (
            state: ConversationsState,
            action: PayloadAction<{ message: IChatMessage; chatId: string }>,
        ) => {
            const { message, chatId } = action.payload;
            updateConversation(state, chatId, message);
        },
        /*
         * updateUserIsTyping() and updateUserIsTypingFromServer() both update a user's typing state.
         * However they are for different purposes. The former action is for updating an user's typing state from
         * the webapp and will be captured by the SignalR middleware and the payload will be broadcasted to all clients
         * in the same group.
         * The updateUserIsTypingFromServer() action is triggered by the SignalR middleware when a state is received
         * from the webapi.
         */
        updateUserIsTyping: (
            state: ConversationsState,
            action: PayloadAction<{ userId: string; chatId: string; isTyping: boolean }>,
        ) => {
            const { userId, chatId, isTyping } = action.payload;
            updateUserTypingState(state, userId, chatId, isTyping);
        },
        updateUserIsTypingFromServer: (
            state: ConversationsState,
            action: PayloadAction<{ userId: string; chatId: string; isTyping: boolean }>,
        ) => {
            const { userId, chatId, isTyping } = action.payload;
            updateUserTypingState(state, userId, chatId, isTyping);
        },
        updateBotResponseStatus: (
            state: ConversationsState,
            action: PayloadAction<{ chatId: string; status: string | undefined }>,
        ) => {
            const { chatId, status } = action.payload;
            const conversation = state.conversations[chatId];
            conversation.botResponseStatus = status;
        },
        updateConversationTitle: (
            state: ConversationsState,
            action: PayloadAction<{ chatId: string; title: string }>,
        ) => {
            const { chatId, title } = action.payload;
            const conversation = state.conversations[chatId];
            conversation.recentAutoRename = true;
            conversation.title = title;
        },
        resetRecentAutoRenameStatus: (state: ConversationsState, action: PayloadAction<{ chatId: string }>) => {
            const { chatId } = action.payload;
            const conversation = state.conversations[chatId];
            conversation.recentAutoRename = false;
        },
        updateMessageProperty: <K extends keyof IChatMessage, V extends IChatMessage[K]>(
            state: ConversationsState,
            action: PayloadAction<{
                property: K;
                value: V;
                chatId: string;
                messageIdOrIndex: string | number;
                updatedContent?: string;
                frontLoad?: boolean;
            }>,
        ) => {
            const { property, value, messageIdOrIndex, chatId, updatedContent, frontLoad } = action.payload;
            const conversation = state.conversations[chatId];
            const conversationMessage =
                typeof messageIdOrIndex === 'number' && conversation.messages
                    ? conversation.messages[messageIdOrIndex]
                    : conversation.messages?.find((m) => m.id === messageIdOrIndex);

            if (conversationMessage) {
                conversationMessage[property] = value;
                if (updatedContent) {
                    conversationMessage.content = updatedContent;
                }
            }

            if (frontLoad) {
                frontLoadChat(state, chatId);
            }
        },
        showIntelligentFilteringMessage: (
            state: ConversationsState,
            action: PayloadAction<{ chatId: string; bucketId: string }>,
        ) => {
            const { chatId, bucketId } = action.payload;
            const conversation = state.conversations[chatId];
            conversation.intelligentFilteringBucketId = bucketId;
        },
        hideIntelligentFilteringMessage: (state: ConversationsState, action: PayloadAction<{ chatId: string }>) => {
            const { chatId } = action.payload;
            const conversation = state.conversations[chatId];
            conversation.intelligentFilteringBucketId = '';
        },
        resetIntelligentFilteringMessage: (state: ConversationsState, action: PayloadAction<{ chatId: string }>) => {
            const { chatId } = action.payload;
            const conversation = state.conversations[chatId];
            conversation.intelligentFilteringBucketId = undefined;
        },
        deleteConversation: (state: ConversationsState, action: PayloadAction<string>) => {
            const keys = Object.keys(state.conversations);
            const id = action.payload;

            // If the conversation being deleted is the selected conversation, select the first remaining conversation
            if (id === state.selectedId) {
                if (keys.length > 1) {
                    state.selectedId = id === keys[0] ? keys[1] : keys[0];
                } else {
                    state.selectedId = '';
                }
            }

            const { [id]: _, ...rest } = state.conversations;
            state.conversations = rest;
        },
        disableConversation: (state: ConversationsState, action: PayloadAction<string>) => {
            const id = action.payload;
            state.conversations[id].disabled = true;
            frontLoadChat(state, id);
            return;
        },
        updatePluginState: (state: ConversationsState, action: PayloadAction<UpdatePluginStatePayload>) => {
            const { id, pluginName, newState } = action.payload;
            const isPluginEnabled = state.conversations[id].enabledHostedPlugins.find((p) => p === pluginName);
            if (newState) {
                if (isPluginEnabled) {
                    return;
                }
                state.conversations[id].enabledHostedPlugins.push(pluginName);
            } else {
                if (!isPluginEnabled) {
                    return;
                }
                state.conversations[id].enabledHostedPlugins = state.conversations[id].enabledHostedPlugins.filter(
                    (p) => p !== pluginName,
                );
            }
        },
        setImportedImagesToConversation: (
            state: ConversationsState,
            action: PayloadAction<{ chatId: string; message: IChatMessage }>,
        ) => {
            const { message, chatId } = action.payload;
            updateConversation(state, chatId, message);
        },
    },
});

const frontLoadChat = (state: ConversationsState, id: string) => {
    const conversation = state.conversations[id];
    const { [id]: _, ...rest } = state.conversations;
    state.conversations = { [id]: conversation, ...rest };
};

const updateConversation = (state: ConversationsState, chatId: string, message: IChatMessage) => {
    state.conversations[chatId].messages?.push({
        ...message,
    });
    frontLoadChat(state, chatId);
};

const updateUserTypingState = (state: ConversationsState, userId: string, chatId: string, isTyping: boolean) => {
    const conversation = state.conversations[chatId];
    const user = conversation.users.find((u) => u.id === userId);
    if (user) {
        user.isTyping = isTyping;
    }
};

// function to update messages of a conversation
const updateConversationMessages = (state: ConversationsState, chatId: string, messages: IChatMessage[]) => {
    state.conversations[chatId].messages = messages;
    frontLoadChat(state, chatId);
};

export const {
    setConversations,
    setConversationMessages,
    setConversationParticipants,
    setConversationUserFeedback,
    setChatMemorySources,
    setChatMemorySourceUploadComplete,
    editConversationTitle,
    editConversationScope,
    editConversationBotProfilePicture,
    editConversationSiteSource,
    editConversationInput,
    editConversationMemoryBalance,
    editConversationIsSaved,
    setSelectedConversation,
    toggleMultiUserConversations,
    addConversation,
    addUserFeedback,
    addMessageToConversationFromUser,
    addMessageToConversationFromServer,
    updateMessageProperty,
    updateUserIsTyping,
    updateUserIsTypingFromServer,
    updateBotResponseStatus,
    setUsersLoaded,
    deleteConversation,
    disableConversation,
    updatePluginState,
    setConversationAsHidden,
    setIsLoadingMessages,
    setIsLoadingChats,
    setInitialChatsLoaded,
    addMessagesToConversation,
    editConversationAuthoritiveDocuments,
    hideIntelligentFilteringMessage,
    resetIntelligentFilteringMessage,
    resetRecentAutoRenameStatus,
    setImportedImagesToConversation,
    updateMessageCitations,
} = conversationsSlice.actions;

export default conversationsSlice.reducer;
